import React from 'react';
import { Check, Gift } from 'lucide-react';

// ── Skeleton (loading placeholder block) ─────────────────────────────
export const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse rounded-md bg-black/[0.06] ${className}`} />
);

// Back is no longer a link above the content — every wizard step carries it in
// the WizardNav footer bar below, so BackLink has no callers left.

/**
 * Flat marketplace primitives that mirror the breakfast handoff mockups
 * (white cards, hairline 0.5px borders, no shadow/lift, 8–12px radii, medium
 * type, muted greys) — with our teal brand accent swapped in for the mockup's
 * blue. Kept separate from the enterprise `Card`/`Button` so the design system
 * is untouched.
 *
 * Mockup token map:
 *   --border      rgba(0,0,0,0.12)  →  border-black/10
 *   --border-md   rgba(0,0,0,0.22)  →  border-black/20
 *   --bg-2        #f5f5f3
 *   --blue        #1B4F8A/#185FA5   →  primary (#07E5D1)  [teal swap]
 *   green         #3B6D11/#EAF3DE   →  emerald (success ticks)
 */

/**
 * Touch-width hit area. The mockups' controls are 34–40px tall, which is under the
 * 44px minimum finger target, so on phones we grow the HIT AREA only (min-height —
 * padding and type stay put) and hand the compact mockup height back at md.
 */
export const TOUCH_TARGET = 'min-h-[44px] md:min-h-0';

// ── Card ─────────────────────────────────────────────────────────────
export const MktCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  selected?: boolean;
  onClick?: () => void;
}> = ({ children, className = '', selected = false, onClick }) => (
  <div
    onClick={onClick}
    // Keyboard/AT support when the card is interactive (matches its onClick).
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    aria-pressed={onClick ? selected : undefined}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }
        : undefined
    }
    className={[
      'bg-white rounded-xl border transition-colors',
      selected ? 'border-primary bg-[#E8FCFA]' : 'border-black/10',
      onClick
        ? 'cursor-pointer hover:border-black/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
        : '',
      className,
    ].join(' ')}
  >
    {children}
  </div>
);

// ── Button ───────────────────────────────────────────────────────────
type MktButtonVariant = 'primary' | 'secondary' | 'ghost';
export const MktButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: MktButtonVariant;
    fullWidth?: boolean;
  }
> = ({ variant = 'primary', fullWidth = false, className = '', children, disabled, ...props }) => {
  const variants: Record<MktButtonVariant, string> = {
    primary: disabled
      ? 'bg-primary/40 text-white cursor-not-allowed'
      : 'bg-primary text-white hover:bg-[#06D4C2]',
    secondary: 'border border-black/20 text-[#111] bg-transparent hover:bg-[#f5f5f3]',
    ghost: 'text-[#6b6b6b] hover:text-[#111] bg-transparent',
  };
  return (
    <button
      disabled={disabled}
      className={[
        'inline-flex items-center justify-center gap-1.5 rounded-lg text-sm font-medium px-5 py-2.5 transition-colors',
        TOUCH_TARGET,
        variants[variant],
        fullWidth ? 'w-full' : '',
        className,
      ].join(' ')}
      {...props}
    >
      {children}
    </button>
  );
};

// ── PillSkeletons (loading placeholders for pill/chip rows) ──────────
/**
 * Pill-shaped placeholders for the sub-category / archetype rows. Those lists
 * hang off slow calls (/sub-categories measured 3.6s, once 15.6s), and without
 * these the step renders an empty void that reads as "broken" rather than
 * "loading". Widths vary so it doesn't look like a progress bar.
 */
const SKELETON_PILL_WIDTHS = ['w-24', 'w-32', 'w-20', 'w-28', 'w-36', 'w-24'];
export const PillSkeletons: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <span className="contents" aria-busy="true" aria-label="Loading options">
    {Array.from({ length: count }).map((_, i) => (
      // Tracks Pill's touch-width height so the row doesn't jump when the real pills land.
      <Skeleton
        key={i}
        className={`h-[44px] md:h-[34px] rounded-lg ${SKELETON_PILL_WIDTHS[i % SKELETON_PILL_WIDTHS.length]}`}
      />
    ))}
  </span>
);

// ── WizardNav (footer Back / Continue pair) ──────────────────────────
/**
 * Bottom navigation for the study wizard: an outlined Back next to the filled
 * Continue, so both moves live in one place at the end of the page instead of
 * a top-left link and a bottom-right CTA.
 *
 * This REPLACES the per-screen BackLink rather than adding to it — there is
 * still exactly one way to go back, so the two affordances never compete.
 * `hint` renders left of the buttons for the contextual copy a step needs to
 * explain a disabled Continue.
 */
export const WizardNav: React.FC<{
  onBack?: () => void;
  backLabel?: string;
  /** Omit on steps where the embedded form owns its own submit button (the
   *  survey / packaging forms) — the bar then carries Back alone. */
  onContinue?: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  hint?: React.ReactNode;
}> = ({
  onBack,
  backLabel = 'Back',
  onContinue,
  continueLabel = 'Continue',
  continueDisabled = false,
  hint,
}) => (
  // Full width on mobile (the rail is a drawer there); from md up it starts AFTER the
  // fixed 220px sidebar so it never runs underneath it.
  <div className="fixed inset-x-0 bottom-0 z-20 border-t border-black/10 bg-white md:left-[220px]">
    <div className="mx-auto flex max-w-[1100px] flex-col gap-2 px-4 py-3 md:flex-row md:flex-wrap md:items-center md:justify-end md:gap-3 md:px-8 md:py-4">
      {hint && <div className="text-[12px] text-[#6b6b6b] md:mr-auto">{hint}</div>}
      {/* Thumb-sized, full-width buttons on a phone; the desktop row is unchanged. */}
      <div className="flex gap-2 md:contents">
        {onBack && (
          <MktButton variant="secondary" onClick={onBack} className="flex-1 md:flex-none">
            {backLabel}
          </MktButton>
        )}
        {onContinue && (
          <MktButton
            disabled={continueDisabled}
            onClick={onContinue}
            className="flex-1 md:flex-none"
          >
            {continueLabel}
          </MktButton>
        )}
      </div>
    </div>
  </div>
);

// ── Eyebrow (uppercase tracked label) ────────────────────────────────
export const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div
    className={`text-[11px] font-medium uppercase tracking-[0.05em] text-[#6b6b6b] ${className}`}
  >
    {children}
  </div>
);

// ── Sub-category pill ────────────────────────────────────────────────
export const Pill: React.FC<{
  active?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
  showCheck?: boolean;
}> = ({ active = false, onClick, children, showCheck = false }) => (
  <button
    onClick={onClick}
    aria-pressed={active}
    className={[
      'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] whitespace-nowrap transition-colors border',
      TOUCH_TARGET,
      active
        ? 'bg-primary text-white border-primary'
        : 'bg-white text-[#6b6b6b] border-black/20 hover:border-black/40',
    ].join(' ')}
  >
    {active && showCheck && <Check className="h-3 w-3" />}
    {children}
  </button>
);

// ── Teal mini tag ────────────────────────────────────────────────────
export const Tag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-[#E8FCFA] px-2 py-0.5 text-[11px] font-medium text-[#0B7C74]">
    {children}
  </span>
);

// ── Checkbox (16px) ──────────────────────────────────────────────────
export const Checkbox: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span
    className={[
      'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border-[1.5px] transition-colors',
      checked ? 'bg-primary border-primary' : 'border-black/20 bg-white',
    ].join(' ')}
  >
    {checked && <Check className="h-2.5 w-2.5 text-white" />}
  </span>
);

// ── Free badge (emerald) ─────────────────────────────────────────────
export const FreeBadge: React.FC<{ children?: React.ReactNode }> = ({
  children = '1 free study remaining',
}) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">
    <Gift className="h-3 w-3" />
    {children}
  </span>
);

// ── Price tag (right-aligned "Price per Twin" block) ─────────────────
// Pass `cents` (the live twin_unit_price_cents from the backend) so the price
// tracks the server, not a hardcoded dollar figure.
export const PriceTag: React.FC<{ cents?: number; amount?: string; unit?: string }> = ({
  cents,
  amount,
  unit = 'Price per Twin',
}) => (
  <div className="text-right flex-shrink-0">
    <div className="text-[12px] text-[#6b6b6b]">{unit}</div>
    <div className="text-[20px] font-medium text-[#111]">
      {cents != null ? `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}` : (amount ?? '')}
    </div>
  </div>
);

// ── Study status badge ───────────────────────────────────────────────
// Centralises status labels so chat (interactive, never "running") and the
// sim use-cases read correctly across the dashboard + history list.
export interface StudyLike {
  use_case?: string;
  status?: string;
  report_status?: string;
  queries_used?: number | null;
  queries_allowed?: number | null;
  queries_remaining?: number | null;
}

/** The buckets a study can be filtered by. Colours follow the design: ready=green,
 *  running=blue, failed=red, draft=amber, chat=teal. */
export type StudyKind = 'ready' | 'running' | 'failed' | 'draft' | 'active' | 'done';

export function studyStatus(s: StudyLike): { label: string; cls: string; kind: StudyKind } {
  const emerald = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  const blue = 'bg-blue-50 text-blue-700 border-blue-200';
  const teal = 'bg-[#E8FCFA] text-[#0B7C74] border-[#07E5D1]/30';
  const amber = 'bg-amber-50 text-amber-800 border-amber-200';
  const red = 'bg-red-50 text-red-700 border-red-200';
  const gray = 'bg-gray-50 text-gray-600 border-gray-200';

  // 'draft' covers both an unfinished wizard AND an abandoned checkout: in both cases
  // nothing has run and the job is to FINISH it, so they filter and act together.
  if (s.status === 'draft') return { label: 'Draft', cls: amber, kind: 'draft' };
  if (s.status === 'pending_payment') return { label: 'Draft', cls: amber, kind: 'draft' };

  if (s.use_case === 'chat') {
    // Chat is interactive — it stays usable until the query budget is spent.
    if (s.queries_allowed != null && (s.queries_remaining ?? 0) <= 0) {
      return { label: 'Complete', cls: gray, kind: 'done' };
    }
    return { label: 'Active', cls: teal, kind: 'active' };
  }
  if (s.status === 'report_ready' || s.report_status === 'ready') {
    return { label: 'Ready', cls: emerald, kind: 'ready' };
  }
  if (s.status === 'failed' || s.report_status === 'failed' || s.report_status === 'error') {
    return { label: 'Failed', cls: red, kind: 'failed' };
  }
  // 'delayed' is what the report task emits when a run outruns its window — still
  // working, not broken.
  if (s.report_status === 'delayed') return { label: 'Taking longer', cls: blue, kind: 'running' };
  if (s.status === 'running') return { label: 'Running', cls: blue, kind: 'running' };
  return { label: (s.status || '').replace(/_/g, ' ') || '—', cls: gray, kind: 'done' };
}

export const StatusBadge: React.FC<{ study: StudyLike }> = ({ study }) => {
  const { label, cls } = studyStatus(study);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${cls}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
};

// Where opening a study from history/dashboard should go. Status wins over
// use-case: an unpaid study resumes checkout (Review) rather than dead-ending on
// a chat/results screen it can't show.
export function studyHref(
  study: StudyLike,
  id: number,
): { path: string; state?: { studyId: number } } {
  if (study.status === 'pending_payment') {
    return { path: '/marketplace/run/review', state: { studyId: id } };
  }
  if (study.use_case === 'chat') return { path: `/marketplace/studies/${id}/chat` };
  return { path: `/marketplace/studies/${id}/results` };
}

// ── Emerald check row (benefit list item) ────────────────────────────
export const CheckRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-start gap-2.5">
    <Check className="mt-0.5 h-[15px] w-[15px] flex-shrink-0 text-emerald-600" />
    <span className="text-[12px] text-[#6b6b6b] leading-snug">{children}</span>
  </div>
);
